const MIN_YEAR = 1900;

export const getMonthName = (monthNumber: number, locales: Intl.LocalesArgument = []): string => {
  const date = new Date();
  date.setDate(1); // define date number (PLAT-2445)
  date.setMonth(monthNumber - 1);

  return date.toLocaleString(locales, { month: 'long' });
};

export const fillYearsOptions = () => {
  const date = new Date();
  const years = [];

  for (let year = date.getFullYear(); year >= MIN_YEAR; year--) {
    years.push(year);
  }

  return years;
};

export const dateIsValid = (day: number, month: number, year: number): boolean => {
  const date = new Date();
  // check min
  if (day === 0 || month === 0 || year < MIN_YEAR) {
    return false;
  }
  // check max
  if (day > 31 || month > 12 || year > date.getFullYear()) {
    return false;
  }

  // check february
  if (month === 2) {
    // check year bissextile
    if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
      return day <= 29;
    } else {
      return day <= 28;
    }
  }

  // less than 31 days months
  if ([4, 6, 9, 11].includes(month)) {
    return day < 31;
  }

  return true;
};

export const userIsMajor = (date: string): boolean => {
  const currentDate = new Date();
  // Subtract 18 years
  const dateLimit = new Date(currentDate);
  dateLimit.setFullYear(currentDate.getFullYear() - 18);

  return new Date(date) < dateLimit;
};

export const hasSixMonthsExpired = (date: Date | string): boolean => {
  const SIX_MONTHS_IN_MILISECONDS = 183 * 24 * 60 * 60 * 1000;
  const isSixMonthsExpired = new Date().getTime() - new Date(date).getTime() >= SIX_MONTHS_IN_MILISECONDS;

  return isSixMonthsExpired;
};

/**
 * Calculates the difference in hours between two dates, rounded to the nearest integer.
 *
 * @param {Date | string} startDate - The start date. Can be a Date object or a date string.
 * @param {Date | string} endDate - The end date. Can be a Date object or a date string.
 * @returns {number} The difference in hours between the two dates, rounded to the nearest integer.
 *
 * @example
 * // Returns 3
 * getDifferenceInHours('2023-10-01T12:00:00Z', '2023-10-01T15:00:00Z');
 *
 * @example
 * // Returns 4
 * getDifferenceInHours('2023-10-01T12:00:00Z', '2023-10-01T15:30:00Z');
 *
 * @example
 * // Returns -3
 * getDifferenceInHours('2023-10-01T15:00:00Z', '2023-10-01T12:00:00Z');
 */
export const getDifferenceInHoursRounded = (startDate: Date | string, endDate: Date | string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const differenceInMilliseconds = end.getTime() - start.getTime();
  const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);
  return Math.round(differenceInHours);
};

/**
 * Checks if a user is considered "new" based on their inscription date and a configurable threshold.
 *
 * @param {string | Date | null | undefined} inscriptionDate - The user's inscription date. Can be a Date object, date string, or null/undefined.
 * @param {number} thresholdDays - The number of days threshold to consider a user as "new".
 * @returns {boolean} True if the user is considered new (within the threshold), false otherwise.
 *
 * @example
 * // Returns true if user registered within the last 31 days
 * isNewUser('2023-12-01T10:00:00Z', 31);
 *
 * @example
 * // Returns false if user registered more than 60 days ago
 * isNewUser('2023-10-01T10:00:00Z', 60);
 *
 * @example
 * // Returns false if no inscription date is provided
 * isNewUser(null, 31);
 */
export const isNewUser = (inscriptionDate: string | Date | null | undefined, thresholdDays: number): boolean => {
  if (!inscriptionDate) {
    return false;
  }

  const inscription = new Date(inscriptionDate);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - inscription.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays <= thresholdDays;
};
